import { useDebouncedCallback } from "./useDebouncedCallback.js";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { IconClose, IconSearch } from "@synthesis/shared-icons";
import { cn } from "@synthesis/shared-utils";
import { jsx, jsxs } from "react/jsx-runtime";
//#region src/SearchBar/index.tsx
const containerSizeClassName = {
	xs: "h-6 rounded-lg px-1.5",
	sm: "h-8 rounded-xl px-3",
	md: "h-10 rounded-[0.875rem] px-3",
	lg: "h-12 rounded-[0.875rem] px-3"
};
const inputSizeClassName = {
	xs: "text-xs",
	sm: "text-sm",
	md: "text-base",
	lg: "text-base"
};
const iconSizeClassName = {
	xs: "size-5",
	sm: "size-5",
	md: "size-6",
	lg: "size-6"
};
/**
* SearchBar из UI Kit.
* @see https://www.figma.com/file/lixEouzSvWhAf04wpHXkIo — РЖД UI Kit
*/
const SearchBar = forwardRef(({ placeholder = "Поиск...", size = "md", disabled = false, clearable = false, readonly = false, isFullWidth = false, debounceTime = 250, manualTrigger = false, onChange, onClear, className, defaultValue, clearInput = false, value, id, disableEnterSubmit, autoFocus = false }, ref) => {
	const inputRef = useRef(null);
	const isControlled = value !== void 0;
	const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? "");
	const hasValue = (isControlled ? value : uncontrolledValue).length > 0;
	const onInternalChange = useCallback((nextValue) => {
		onChange?.(nextValue);
	}, [onChange]);
	const debouncedOnChange = useDebouncedCallback(onInternalChange, debounceTime);
	const setValue = useCallback((nextValue) => {
		if (!isControlled) setUncontrolledValue(nextValue);
		if (inputRef.current) inputRef.current.value = nextValue;
	}, [isControlled]);
	const handleReset = useCallback(() => {
		setValue("");
		onChange?.("");
		onClear?.();
	}, [
		onChange,
		onClear,
		setValue
	]);
	const handleChange = (event) => {
		const nextValue = event.target.value;
		if (!isControlled) setUncontrolledValue(nextValue);
		if (!manualTrigger) debouncedOnChange(nextValue);
	};
	const handleKeyDown = (event) => {
		if (disableEnterSubmit && event.key === "Enter") event.preventDefault();
		if (event.key === "Enter") onChange?.(inputRef.current?.value ?? "");
	};
	const handleSearchIconClick = () => {
		debouncedOnChange(inputRef.current?.value ?? "");
	};
	useImperativeHandle(ref, () => inputRef.current, []);
	useEffect(() => {
		if (defaultValue && !manualTrigger) onInternalChange(defaultValue);
	}, []);
	useEffect(() => {
		if (clearInput) handleReset();
	}, [clearInput, handleReset]);
	useEffect(() => {
		if (value !== void 0 && inputRef.current) inputRef.current.value = value;
	}, [value]);
	return /* @__PURE__ */ jsxs("div", {
		"data-cy": "searchBar-container",
		className: cn("bg-neutrals-95 focus-within:outline-system-info-70 flex items-center gap-2 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-1", containerSizeClassName[size], isFullWidth && "w-full", disabled && "bg-neutrals-80", readonly && "bg-neutrals-80", className),
		children: [
			/* @__PURE__ */ jsx("button", {
				type: "button",
				"data-cy": "searchBar-styledLeftIconWrapper",
				className: cn("text-neutrals-60 flex shrink-0 items-center justify-center", iconSizeClassName[size]),
				disabled: disabled || readonly,
				onClick: handleSearchIconClick,
				"aria-label": "Поиск",
				children: /* @__PURE__ */ jsx(IconSearch, { className: "size-4" })
			}),
			/* @__PURE__ */ jsx("input", {
				ref: inputRef,
				autoComplete: "off",
				id,
				"data-cy": "searchBar-styledInput",
				className: cn("text-type-common placeholder:text-type-light w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap bg-transparent outline-none", inputSizeClassName[size], disabled && "placeholder:text-[#998E8E]"),
				defaultValue,
				readOnly: readonly,
				placeholder,
				onKeyDown: handleKeyDown,
				disabled,
				onChange: handleChange,
				type: "text",
				autoFocus
			}),
			clearable && !readonly && hasValue ? /* @__PURE__ */ jsx("button", {
				type: "button",
				"aria-label": "Очистить",
				className: "text-neutrals-60 hover:text-neutrals-60/80 size-5 shrink-0",
				onClick: handleReset,
				children: /* @__PURE__ */ jsx(IconClose, {
					"data-cy": "searchBar-iconClose",
					className: "text-type-minor size-full"
				})
			}) : null
		]
	});
});
SearchBar.displayName = "SearchBar";
//#endregion
export { SearchBar };

//# sourceMappingURL=index.js.map